import { Configuration } from '@icc/shared/types';
import { PriceSegment, DiscountGroups, PricePart } from '@icc/price/b2b';

import { CoupledWindowActiveConfiguration } from './coupled-window-active-configuration';
import { CouplingActive, SideProfileActive, Profile, WindowParameters } from '@icc/window-door/types';
import { Accessory } from '@icc/construction-elements/types';
import { RollerShutter } from '@icc/shutter/types';
import { WindowInCoupling, RollerShutterInCoupling } from '../elements/coupled-window';
import { Sill } from '@icc/window-door/types';
import { changesInVersion2 } from './converters/to-version-2';
import { core } from '@icc/shared/helpers';

export class CoupledWindowConfiguration implements Configuration {
    $version = 2;
    type: 'coupled_window' = 'coupled_window';
    name = '';
    price: number | null = null;
    listPrice: number | null = null;
    priceNoMargin: number | null = null;
    listPriceNoMargin: number | null = null;
    priceAfterDiscountsInCurrency: number | null = null;
    listPriceAfterDiscountsInCurrency: number | null = null;
    priceBeforePromotions: number | null = null;
    listPriceBeforePromotions: number | null = null;
    priceGross: number | null = null;
    listPriceGross: number | null = null;
    priceGrossAfterDiscounts: number | null = null;
    listPriceGrossAfterDiscounts: number | null = null;
    priceGrossBeforePromotions: number | null = null;
    listPriceGrossBeforePromotions: number | null = null;
    quantity: number = 1;
    priceSegments: PriceSegment[] = [];
    listPriceSegments: PriceSegment[] = [];
    priceSegmentsNoMargin: PriceSegment[] = [];
    listPriceSegmentsNoMargin: PriceSegment[] = [];
    priceSegmentsBeforePromotions: PriceSegment[] = [];
    listPriceSegmentsBeforePromotions: PriceSegment[] = [];
    priceParts: PricePart[] = [];
    listPriceParts: PricePart[] = [];
    pricePartsNoMargin: PricePart[] = [];
    listPricePartsNoMargin: PricePart[] = [];
    pricePartsBeforePromotions: PricePart[] = [];
    listPricePartsBeforePromotions: PricePart[] = [];
    discountGroups: DiscountGroups = {};
    discountGroupsNoMargin: DiscountGroups = {};
    title = '';
    description = '';
    attachments: any[] = [];
    deletedAttachments: any[] = [];
    valid: {} = {};
    parameters: WindowParameters = {
        weight: null,
    };
    attributes: {
        id: string;
        name: string;
        value: string;
        valueId?: number;
    }[] = [];

    height: number = 1500;
    width: number = 1500;
    windows: WindowInCoupling[] = [];
    couplings: CouplingActive[] = [];
    sideProfiles: SideProfileActive[] = [];
    sideAccessories: Accessory[] = [];
    rollerShutters: RollerShutterInCoupling[] = [];
    windowSills: Sill[] = [];

    usedProfiles: Profile[] = [];

    static converters = {
        2: changesInVersion2,
    };

    constructor(
        configuration?,
        dataRequiredToUpdate?: any,
        conversion = true,
        configuratorsDataService?,
        private langCode = configuration?.langCode || 'pl'
    ) {
        if (CoupledWindowConfiguration.is(configuration) && conversion) {
            this.runConversion(
                configuration,
                Object.assign(dataRequiredToUpdate, {
                    langCode: this.langCode,
                })
            );
        } else {
            if (CoupledWindowActiveConfiguration.is(configuration)) {
                this.name = configuration.Name;
                this.price = configuration.Price;
                this.listPrice = configuration.ListPrice;
                this.priceNoMargin = configuration.PriceNoMargin;
                this.listPriceNoMargin = configuration.ListPriceNoMargin;
                this.priceAfterDiscountsInCurrency = configuration.PriceAfterDiscountsInCurrency || configuration.Price;
                this.listPriceAfterDiscountsInCurrency = configuration.ListPriceAfterDiscountsInCurrency || configuration.ListPrice;
                this.priceGross = configuration.PriceGross || configuration.Price;
                this.listPriceGross = configuration.PriceGross || configuration.ListPrice;
                this.priceGrossAfterDiscounts = configuration.PriceGrossAfterDiscounts || configuration.Price;
                this.listPriceGrossAfterDiscounts = configuration.PriceGrossAfterDiscounts || configuration.ListPrice;
                this.priceBeforePromotions = configuration.PriceBeforePromotions;
                this.listPriceBeforePromotions = configuration.ListPriceBeforePromotions;
                this.quantity = configuration.Quantity;
                this.priceSegments = configuration.PriceSegments;
                this.listPriceSegments = configuration.ListPriceSegments;
                this.priceSegmentsNoMargin = configuration.PriceSegmentsNoMargin;
                this.listPriceSegmentsNoMargin = configuration.ListPriceSegmentsNoMargin;
                this.priceSegmentsBeforePromotions = configuration.PriceSegmentsBeforePromotions;
                this.listPriceSegmentsBeforePromotions = configuration.ListPriceSegmentsBeforePromotions;
                this.priceParts = configuration.PriceParts;
                this.listPriceParts = configuration.ListPriceParts;
                this.pricePartsNoMargin = configuration.PricePartsNoMargin;
                this.listPricePartsNoMargin = configuration.ListPricePartsNoMargin;
                this.pricePartsBeforePromotions = configuration.PricePartsBeforePromotions;
                this.listPricePartsBeforePromotions = configuration.ListPricePartsBeforePromotions;
                this.discountGroups = configuration.DiscountGroups;
                this.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
                this.title = configuration.Title;
                this.description = configuration.Description;
                this.attachments = configuration.Attachments;
                this.valid = configuration.valid;
                this.attributes = configuration.attributes;

                this.parameters = configuration.parameters;

                this.windows = configuration.windows;
                this.height = configuration.height;
                this.width = configuration.width;
                this.rollerShutters = configuration.rollerShutters;

                this.couplings = configuration.couplings;
                this.sideProfiles = configuration.sideProfiles;
                this.windowSills = configuration.windowSills;
                this.usedProfiles = configuration.usedProfiles;
            }
        }
    }

    public static is(configuration): configuration is CoupledWindowConfiguration {
        return configuration instanceof CoupledWindowConfiguration || configuration.$version;
    }

    protected runConversion(configuration, dataRequiredToUpdate: any) {
        let updatedConfiguration = core.copy(configuration);

        for (let version = configuration.$version + 1; version <= this.$version; version++) {
            updatedConfiguration.$version = version;
            if (CoupledWindowConfiguration.converters[version] && typeof CoupledWindowConfiguration.converters[version] === 'function') {
                updatedConfiguration = CoupledWindowConfiguration.converters[version](
                    updatedConfiguration,
                    dataRequiredToUpdate
                );
            }
        }

        (Object as any)
            .entries(updatedConfiguration)
            .forEach(([key, value]) => {
                this[key] = value;
            });
    }
}

export type ICoupledWindowConfiguration = Pick<CoupledWindowConfiguration, keyof CoupledWindowConfiguration>;
